/** 手机使用要求：记录按字段阅读，金额/薪资对比保留横向表；弹窗表单可换行、主要操作便于触控。只调整显示，不改变数据、权限及审批规则。 */
import React, { useEffect, useState, useCallback } from "react";
import { useApp } from "../../context/AppContext";
import { toast } from "../../components/ui/sonner";
import { Loader2, AlertTriangle, Check, Pencil, Plus, RotateCcw, FileDown } from "lucide-react";
import { buildTree, EntityTreeView, EntityDialog, EntityDeleteDialog, LevelSummary, nodeStats } from "./EntityTree";
import { OrgDeptCard } from "./OrgDeptCard";
import { DeptDialog, AssignDialog, MoveDialog } from "./OrgDeptEditor";
import { VacancyDialog } from "./OrgVacancyDialog";
import { ResolveStaffDialog } from "./OrgResolveStaff";

const blankEntity = (parentId) => ({ id: "", zh: "", en: "", legalName: "", leader: "", leaderUsername: "", digestCcEng: false, note: "", parentId });

export default function HRMSOrg() {
  const { api, lang } = useApp();
  const zh = lang === "zh";
  const T = (z, e) => (zh ? z : e);
  const [org, setOrg] = useState(null);
  const [tab, setTab] = useState(() => new URLSearchParams(window.location.search).get("branch") || "");
  const [edit, setEdit] = useState(null); // {branch, role, value}
  const [editing, setEditing] = useState(false);
  const [entDlg, setEntDlg] = useState(null);
  const [delTarget, setDelTarget] = useState(null);
  const [roleCatalog, setRoleCatalog] = useState([]);
  const [deptDlg, setDeptDlg] = useState(null); // {dept|null}
  const [assignDlg, setAssignDlg] = useState(null); // {dept, pos}
  const [moveEmp, setMoveEmp] = useState(null);
  const [vacDlg, setVacDlg] = useState(null); // {dept, pos}
  const [resolveTask, setResolveTask] = useState(null); // {pending, removed, deleteDept}
  const [exportBusy, setExportBusy] = useState(false);

  const loadCatalog = useCallback(() => api.get("/hrms/role-catalog").then((r) => setRoleCatalog(r.data.filter((x) => x.role !== "subcontractor"))).catch(() => {}), [api]);
  const reload = useCallback(() => {
    api.get("/hrms/org").then(({ data }) => {
      setOrg(data);
      setTab((t) => t || data.branches[0]?.id || "");
      if (data.canEditOrg) loadCatalog();
    }).catch(() => setOrg({ branches: [], alerts: [] }));
  }, [api, loadCatalog]);
  useEffect(() => { reload(); }, [reload]);

  const saveHc = async () => {
    try {
      await api.put("/hrms/headcount", { branch: edit.branch, role: edit.role, approved: Number(edit.value || 0) });
      toast.success(T("编制已更新", "Headcount updated")); setEdit(null); reload();
    } catch (e) { toast.error(e?.response?.data?.detail || T("保存失败", "Save failed")); }
  };

  if (!org) return <div className="p-8 text-center text-slate-400"><Loader2 className="h-5 w-5 animate-spin inline" /></div>;
  const br = org.branches.find((b) => b.id === tab);
  const root = org.branches.find((b) => b.id === "hq");
  const kids = buildTree(org.branches);
  const canEditOrg = !!org.canEditOrg;
  // canFullTree：法人节点的「新增 / 新增下级 / 上移 / 下移 / 删除」权限（后端仅 admin / CEO）。
  // 2026-10-14 修复「假按钮」：这些按钮原先按 org.canManage 渲染，分公司 HR 也能看到，
  // 但后端 _global_only 一律返回 403 —— 看得见、点不动、还弹报错。改为按后端下发的能力位渲染，
  // HR 在法人树上只保留「编辑」（改本公司法人信息，后端 PUT 是放行的）。
  const canFullTree = !!org.canFullTree;
  const realDepts = (br?.departments || []).filter((d) => !d.unassigned);

  const reorderDept = async (dp, dir) => {
    const list = realDepts.map((d) => ({ id: d.id, name: d.name, nameEn: d.nameEn, roles: d.positions.map((p) => p.role) }));
    const i = list.findIndex((d) => d.id === dp.id), j = i + dir;
    if (j < 0 || j >= list.length) return;
    [list[i], list[j]] = [list[j], list[i]];
    try { await api.put(`/hrms/org/${br.id}/departments`, { departments: list }); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || T("排序失败", "Reorder failed")); }
  };
  const exportEntityHistory = async () => {
    setExportBusy(true);
    try {
      const res = await api.get("/hrms/employees/history-batch.pdf", { params: { branch: br.id }, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a"); a.href = url; a.download = `员工组织履历合集_${br.zh}.pdf`; document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 30000);
      toast.success(T(`已导出 ${res.headers["x-employee-count"] || ""} 人履历合集`, "Exported"));
    } catch (e) {
      let msg = e?.message; const d = e?.response?.data;
      if (d instanceof Blob) { try { msg = JSON.parse(await d.text()).detail; } catch { /* ignore */ } }
      toast.error(msg || T("导出失败", "Export failed"));
    }
    setExportBusy(false);
  };
  const resetDepts = async () => {
    try { await api.delete(`/hrms/org/${br.id}/departments`); toast.success(T("已恢复集团标准部门", "Reset to default")); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || T("操作失败", "Failed")); }
  };

  const moveEntity = async (b, dir) => {
    const sibs = (kids[b.parentId || "hq"] || []).map((x) => x.id);
    const i = sibs.indexOf(b.id), j = i + dir;
    if (j < 0 || j >= sibs.length) return;
    [sibs[i], sibs[j]] = [sibs[j], sibs[i]];
    try { await api.put("/hrms/entities/order", { ids: sibs }); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || T("排序失败", "Reorder failed")); }
  };
  const openEdit = (b) => setEntDlg({ id: b.id, zh: b.zh, en: b.en || "", legalName: b.legalName || "", leader: b.leader || "", leaderUsername: b.leaderUsername || "", digestCcEng: !!b.digestCcEng, note: b.note || "", parentId: b.parentId || "hq" });

  return (
    <div className="space-y-4" data-testid="hrms-org-page">
      <div>
        <h1 className="text-xl font-extrabold text-slate-800">{T("组织架构与编制", "Org Structure & Headcount")}</h1>
        <p className="text-xs text-slate-400 mt-0.5">{T("法人/分公司 → 部门 → 岗位（绑定角色职责 JD）· 批准编制 vs 实际在编 · 超编/缺编预警", "Entity/branch → department → position (with role JD) · approved vs actual headcount · over/under-staffing alerts")}</p>
      </div>

      {root && (
        <div className={`rounded-xl border bg-white p-4 ${editing ? "border-indigo-300 ring-1 ring-indigo-100" : "border-slate-200"}`} data-testid="hrms-legal-tree">
          <div className="flex items-start gap-2 mb-3">
            <div className="flex-1">
              <p className="text-sm font-bold text-slate-700">{T("法人树 · 集团合并视图", "Entity Tree · Group View")}</p>
              <p className="text-[11px] text-slate-400">{editing
                ? T("编辑模式：可新增/编辑/排序/删除法人节点，支持多层级（总部 → 分公司 → 子公司）。分公司系统编号不变。", "Edit mode: add / edit / reorder / delete entities; multi-level supported. System ids are kept.")
                : T("节点属性：法人 / 编制 / 实际 / 负责人 / 缺编摘要。点击节点查看该法人部门明细。", "Node shows entity / headcount / actual / leader / staffing gaps. Click a node for department detail.")}</p>
            </div>
            {org.canManage && (
              <div className="flex items-center gap-2">
                {editing && canFullTree && (
                  <button type="button" data-testid="hrms-tree-add-root-child" onClick={() => setEntDlg(blankEntity("hq"))}
                    className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 inline-flex items-center gap-1 text-xs rounded-full border border-indigo-200 text-indigo-700 bg-indigo-50 px-3 py-1.5 font-semibold hover:bg-indigo-100"><Plus className="h-3.5 w-3.5" />{T("新增法人", "New entity")}</button>
                )}
                <button type="button" data-testid="hrms-tree-edit-toggle" onClick={() => setEditing((v) => !v)}
                  className={`min-h-11 min-w-11 md:min-h-0 md:min-w-0 inline-flex items-center gap-1 text-xs rounded-full px-3 py-1.5 font-semibold ${editing ? "bg-slate-800 text-white" : "border border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
                  {editing ? <><Check className="h-3.5 w-3.5" />{T("完成", "Done")}</> : <><Pencil className="h-3.5 w-3.5" />{T("编辑法人树", "Edit tree")}</>}
                </button>
              </div>
            )}
          </div>
          <EntityTreeView root={root} kids={kids} alerts={org.alerts} editing={editing && org.canManage} canFullTree={canFullTree} zh={zh}
            onClick={(b) => setTab(b.id)} onEdit={openEdit} onAdd={(b) => setEntDlg(blankEntity(b.id))} onMove={moveEntity} onDelete={setDelTarget} />
          <LevelSummary root={root} kids={kids} alerts={org.alerts} zh={zh} />
        </div>
      )}

      {org.alerts.length > 0 && (
        <div className="rounded-xl border border-amber-300 bg-amber-50/70 p-3.5" data-testid="hrms-org-alerts">
          <p className="text-xs font-bold text-amber-800 flex items-center gap-1.5 mb-1.5"><AlertTriangle className="h-3.5 w-3.5" />{T(`编制预警 ${org.alerts.length} 项`, `${org.alerts.length} staffing alert(s)`)}</p>
          <div className="flex flex-wrap gap-1.5">
            {org.alerts.map((a, i) => (
              <span key={i} className={`text-[11px] px-2 py-0.5 rounded-full font-semibold ${a.type === "over" ? "bg-red-100 text-red-600" : "bg-amber-100 text-amber-700"}`}>
                {zh ? a.branchZh : (a.branchEn || a.branchZh)} · {zh ? a.roleZh : (a.roleEn || a.roleZh)}：{a.type === "over" ? T("超编", "over") : T("缺编", "under")} {a.actual}/{a.approved}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* 法人切换 chip —— 人数口径统一为「本级」（nodeStats().own.actual）。
          修改原因（2026-10-15 · HR 反馈一-10「Employee headcount is incorrect」）：
            2026-10-14 为解决一-9「同一法人同屏两个数」，曾把 chip 改成「含下级」合计（all.actual）。
            但后端 _br_view 把所有非法人的 parentId 一律补成 hq，于是 5 个法人 chip 里的人各加起来
            = 187 人，而全公司实际在职只有 118 人 —— 下级人数被总部重复计了一次，反而更像「人数算错」。
          最终效果：chip 与本页法人树卡片头部、LevelSummary 汇总（按各法人本级逐层相加）、
            员工列表在职总数四处口径一致，均为「本级」；含下级合计只在法人树卡片次行标注。
          关键取舍：以「本级」为主口径 —— 法人并列，上级不应把下级人数算进自己的头部数字。 */}
      <div className="flex flex-wrap gap-2">
        {org.branches.map((b) => {
          const hc = nodeStats(b, kids, org.alerts).own.actual;
          return (
            <button key={b.id} data-testid={`hrms-org-branch-${b.id}`} data-count={hc} onClick={() => setTab(b.id)}
              className={`min-h-11 min-w-11 md:min-h-0 md:min-w-0 inline-flex items-center gap-1.5 text-xs rounded-full px-3.5 py-1.5 font-semibold ${tab === b.id ? "bg-slate-800 text-white" : "bg-white border border-slate-200 text-slate-500"}`}>
              {zh ? b.zh : (b.en || b.zh)}<span className={`text-[10px] ${tab === b.id ? "text-slate-300" : "text-slate-400"}`}>{hc}{T("人", "")}</span>
            </button>
          );
        })}
      </div>

      {/* 人数口径说明（对应 HR 反馈「组织页人数不对（属地范围 / 账号 region 配错待确认）」）：
          明确三处人数如何统计，避免把「含下级的合计」误读为「本级人数」；
          同时说明 HR 账号按 region 只看所属分公司，数字异常时优先核对账号 region 与员工法人归属。 */}
      <p className="text-[11px] text-slate-400" data-testid="hrms-org-headcount-basis">
        {T("人数口径：法人 chip 与法人树节点均显示「本级 + 下级法人」合计在编人数（点击 chip 可下钻到该法人明细）；HR 账号仅能看到所属分公司（region）范围的员工，若人数与预期不符，请先核对账号的所属分公司(region)设置与员工的法人归属是否一致。",
           "Headcount basis: entity chips and tree nodes show the combined headcount of the entity plus its sub-entities (click a chip to drill down). HR accounts only see employees within their own entity (region); if numbers look off, first verify the account's region setting and each employee's entity assignment.")}
      </p>

      {br && (
        <div className="space-y-3" data-testid={`hrms-org-detail-${br.id}`}>
          {canEditOrg && (
            <div className="flex flex-wrap items-center gap-2" data-testid="hrms-dept-toolbar">
              <p className="text-xs text-slate-500">{T("部门与人员编辑器", "Departments & staff editor")} · <b className="text-slate-700">{zh ? br.zh : br.en}</b>{br.customDepts && <span className="ml-1.5 text-[10px] px-1.5 py-0.5 rounded-full bg-indigo-50 text-indigo-700" data-testid="hrms-dept-custom-badge">{T("已自定义", "Customized")}</span>}</p>
              <span className="flex-1" />
              <button type="button" data-testid="hrms-dept-export-history" disabled={exportBusy} onClick={exportEntityHistory} title={T("导出本法人全部员工的组织履历 PDF 合集", "Export org-history PDF collection for this entity")}
                className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 inline-flex items-center gap-1 text-xs rounded-full border border-slate-200 text-slate-600 px-3 py-1.5 hover:bg-slate-50 disabled:opacity-50">{exportBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileDown className="h-3.5 w-3.5" />}{T("导出履历合集", "Export histories")}</button>
              {br.customDepts && <button type="button" data-testid="hrms-dept-reset" onClick={resetDepts} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 inline-flex items-center gap-1 text-xs rounded-full border border-slate-200 text-slate-600 px-3 py-1.5 hover:bg-slate-50"><RotateCcw className="h-3.5 w-3.5" />{T("恢复集团标准部门", "Reset to default")}</button>}
              <button type="button" data-testid="hrms-dept-add" onClick={() => setDeptDlg({ dept: null })} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 inline-flex items-center gap-1 text-xs rounded-full bg-indigo-600 hover:bg-indigo-700 text-white font-semibold px-3 py-1.5"><Plus className="h-3.5 w-3.5" />{T("新增部门", "New department")}</button>
            </div>
          )}
          <div className="grid md:grid-cols-2 gap-3">
            {br.departments.map((dp, i) => (
              <OrgDeptCard key={dp.id} dp={dp} br={br} org={org} zh={zh} idx={realDepts.findIndex((d) => d.id === dp.id)} count={realDepts.length}
                edit={edit} setEdit={setEdit} saveHc={saveHc} canEdit={canEditOrg}
                onEditDept={(d) => setDeptDlg({ dept: d })} onAssign={(d, p) => setAssignDlg({ dept: d, pos: p })} onMoveEmp={setMoveEmp} onReorder={reorderDept} onPostVacancy={(d, p) => setVacDlg({ dept: d, pos: p })} />
            ))}
          </div>
        </div>
      )}

      {deptDlg && br && <DeptDialog api={api} zh={zh} branch={br} dept={deptDlg.dept} depts={br.departments} roleCatalog={roleCatalog} onClose={() => setDeptDlg(null)} onSaved={() => { setDeptDlg(null); reload(); }}
        onCatalogChange={loadCatalog} onResolveStaff={(task) => { setDeptDlg(null); setResolveTask(task); }} />}
      {resolveTask && br && <ResolveStaffDialog api={api} zh={zh} branch={br} org={org} task={resolveTask} onClose={() => { setResolveTask(null); reload(); }} onDone={() => { setResolveTask(null); reload(); }} onRefresh={reload} />}
      {assignDlg && br && <AssignDialog api={api} zh={zh} branch={br} dept={assignDlg.dept} pos={assignDlg.pos} onClose={() => setAssignDlg(null)} onSaved={() => { setAssignDlg(null); reload(); }} />}
      {vacDlg && br && <VacancyDialog api={api} zh={zh} branch={br} dept={vacDlg.dept} pos={vacDlg.pos} onClose={() => setVacDlg(null)} onSaved={reload} />}
      {moveEmp && <MoveDialog api={api} zh={zh} emp={moveEmp} org={org} onClose={() => setMoveEmp(null)} onSaved={() => { setMoveEmp(null); reload(); }} />}
      {entDlg && <EntityDialog api={api} dlg={entDlg} branches={org.branches} kids={kids} zh={zh} onClose={() => setEntDlg(null)} onSaved={() => { setEntDlg(null); reload(); }} />}
      {delTarget && <EntityDeleteDialog api={api} target={delTarget} zh={zh} onClose={() => setDelTarget(null)} onDeleted={() => { setDelTarget(null); if (tab === delTarget.id) setTab("hq"); reload(); }} />}
    </div>
  );
}
